import { t } from "@lingui/core/macro";
import graphql from "babel-plugin-relay/macro";
import { endOfMonth, isAfter } from "date-fns";
import { useFragment } from "react-relay";

import { convertCardBrandToIcon } from "@whoosh/design-system/src/components/icons_v2/creditCards";
import { PlusIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { normalizeCardBrand } from "@whoosh/features/src/Square/features/SquareCard/utils/normalizeCardBrand";

import { PaymentMethod_squareCardOnFile$key } from "./__generated__/PaymentMethod_squareCardOnFile.graphql";

type Props = {
  squareCardRef?: PaymentMethod_squareCardOnFile$key | null;
  usageDate?: Date | null;
  onAddPress: () => void;
  onChangePress: () => void;
  active?: boolean;
  canUpdate?: boolean;
};

export const PaymentMethod = ({
  squareCardRef,
  usageDate,
  onAddPress,
  onChangePress,
  active = true,
  canUpdate = true,
}: Props) => {
  const squareCard = useFragment(PaymentMethodFragmentString, squareCardRef);

  const cardExpirationDate = squareCard
    ? endOfMonth(new Date(squareCard.expYear, squareCard.expMonth - 1, 1))
    : null;

  const isCardExpired =
    usageDate && cardExpirationDate
      ? isAfter(usageDate, cardExpirationDate)
      : false;

  return (
    <Box
      p={3}
      borderRadius="2"
      backgroundColor={"surface-primary"}
      borderWidth={4}
      borderColor={active ? "surface-main-primary-subtle" : "surface-secondary"}
      gap={2}
    >
      <HStack gap={1}>
        <Text variant="mainSmallSemiBold">{t`Payment Method`}</Text>
        <Text variant="mainSmallSemiBold" color="text-secondary">
          ({t`Required`})
        </Text>
      </HStack>
      {squareCard ? (
        <VStack>
          <HStack pt={2} justifyContent="space-between" alignItems="center">
            <HStack alignItems="center" gap={2}>
              <Box
                bg="surface-secondary"
                width={32}
                height={20}
                borderRadius="2"
                justifyContent="center"
                alignItems="center"
              >
                {convertCardBrandToIcon(squareCard?.cardBrand)}
              </Box>
              <VStack>
                <HStack gap={1}>
                  <Text variant="mainBaseSemiBold">{t`${normalizeCardBrand(
                    squareCard?.cardBrand ?? ""
                  )} ending ${squareCard?.lastFour}`}</Text>
                </HStack>
                <Text variant="mainTiny" color="text-secondary">
                  {t`Exp`} {squareCard?.expMonth}/{squareCard?.expYear}
                </Text>
              </VStack>
            </HStack>
            {canUpdate ? (
              <Pressable onPress={onChangePress}>
                <Text
                  color="text-main-primary"
                  variant="mainSmallSemiBold"
                >{t`Change`}</Text>
              </Pressable>
            ) : null}
          </HStack>
          {isCardExpired ? (
            <Box p={2}>
              <Text
                variant="mainTiny"
                color="text-error"
                textAlign="center"
              >{t`This card will expire before its usage. Please update your payment method.`}</Text>
            </Box>
          ) : null}
        </VStack>
      ) : (
        <Pressable onPress={onAddPress}>
          <HStack pt={2} gap={2} alignItems="center">
            <Box
              bg="surface-secondary"
              width={40}
              height={26}
              borderRadius="2"
              alignItems="center"
              justifyContent="center"
            >
              <PlusIcon width={16} height={16} color="text-main-primary" />
            </Box>
            <Text
              color="text-main-primary"
              variant="mainBaseMedium"
            >{t`Add New Card`}</Text>
          </HStack>
        </Pressable>
      )}
    </Box>
  );
};

const PaymentMethodFragmentString = graphql`
  fragment PaymentMethod_squareCardOnFile on SquareCardOnFile {
    id
    expMonth
    expYear
    cardBrand
    lastFour
  }
`;
